import type { Country } from "@nh/shared";

/**
 * Los valores del FORMULARIO del alta.
 *
 * Casi `RegisterForm` de `@nh/shared`, con dos diferencias que existen
 * porque un formulario a medio llenar no tiene la forma de un contrato
 * cumplido:
 *
 *  · `acceptedTerms` es `boolean`. Una casilla nace desmarcada, así que
 *    `false` es un estado legítimo mientras se llena; el `true` es lo que
 *    el esquema EXIGE para dejar pasar.
 *  · `country` admite `""`. Un `<select>` sin opción vacía nace con la
 *    primera seleccionada, y alguien que nunca tocó el control acabaría
 *    registrado en México sin haberlo dicho. El hueco vacío hace que "no
 *    elegí" sea un estado distinguible, y el esquema lo rechaza con
 *    "Elige tu país".
 */
export interface RegisterFormValues {
  firstName: string;
  lastName: string;
  country: Country | "";
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptedTerms: boolean;
}

export type RegisterFieldName = keyof RegisterFormValues;

export const EMPTY_REGISTER_FORM: RegisterFormValues = {
  firstName: "",
  lastName: "",
  country: "",
  phone: "",
  email: "",
  password: "",
  confirmPassword: "",
  acceptedTerms: false,
};

export interface RegisterStep {
  /** Lo que se lee en el indicador de progreso. */
  readonly label: string;
  /** Qué campos se capturan en este paso. */
  readonly fields: readonly RegisterFieldName[];
}

/**
 * El alta, paso a paso. **Esta es la única declaración del reparto.**
 *
 * De aquí salen tres cosas que de otro modo se escribirían por separado y
 * acabarían contradiciéndose:
 *
 *  1. Los rótulos del `Stepper`.
 *  2. Qué validar al pulsar "Siguiente" — solo lo de ESTE paso, para que
 *     nadie vea el error de un campo que todavía no ha visto.
 *  3. A qué paso pertenece un error que mandó el servidor, para poder
 *     llevar de vuelta a la persona hasta él.
 *
 * Si el tercero se desincronizara del primero, un `EMAIL_TAKEN` señalado
 * en el paso 3 dejaría su mensaje en un paso oculto: un botón de crear
 * cuenta que no hace nada y nadie sabe por qué.
 *
 * `confirmPassword` vive en el paso 2 aunque no viaje al API: se valida
 * donde se escribe.
 */
export const REGISTER_STEPS: readonly RegisterStep[] = [
  {
    label: "Quién eres",
    fields: ["firstName", "lastName", "country", "phone"],
  },
  {
    label: "Tu acceso",
    fields: ["email", "password", "confirmPassword"],
  },
  {
    label: "Confirmación",
    fields: ["acceptedTerms"],
  },
];

export const REGISTER_STEP_LABELS: readonly string[] = REGISTER_STEPS.map(
  (step) => step.label,
);

/** Todos los campos del formulario, en el orden en que se piden. */
export const REGISTER_FIELDS: readonly RegisterFieldName[] = REGISTER_STEPS.flatMap(
  (step) => step.fields,
);

/**
 * Los campos de un paso. Un índice fuera de rango devuelve la lista vacía
 * en vez de reventar: con `noUncheckedIndexedAccess` el compilador obliga
 * a decidirlo, y "no hay campos que validar" es la respuesta honesta para
 * un paso que no existe.
 */
export function fieldsOfStep(step: number): readonly RegisterFieldName[] {
  return REGISTER_STEPS[step]?.fields ?? [];
}

/** Índices de los pasos, con nombre, para que las pantallas no cuenten. */
export const STEP_IDENTITY = 0;
export const STEP_ACCESS = 1;
export const STEP_CONFIRM = 2;
